import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeaturedProject = ({ project }) => {
  const location = useLocation();
  
  const handleCardClick = () => {
    sessionStorage.setItem(`scroll-${location.pathname}`, window.scrollY.toString());
  };

  if (!project) return null;

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden mb-6 relative">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-50"></div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 relative z-10">
        {/* Project Image */}
        <div className="relative lg:col-span-5 h-64 lg:h-auto min-h-[300px] overflow-hidden">
          <Image
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-4 left-4">
            <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium bg-warning text-white">
              <Icon name="Star" size={12} className="fill-current" />
              <span>Featured Project</span>
            </span>
          </div>
        </div>

        {/* Project Content */}
        <div className="lg:col-span-7 p-6 lg:p-8 flex flex-col justify-center">
          <div className="mb-4">
            <div className="flex items-center space-x-2 text-accent mb-2">
              <Icon name="Zap" size={16} />
              <span className="text-sm font-medium uppercase tracking-wide">
                {project.type}
              </span>
            </div>
            <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-3">
              {project.title}
            </h2>
            <p className="text-text-secondary text-base lg:text-lg leading-relaxed mb-4">
              {project.description}
            </p>
          </div>

          {/* Project Details */}
          {(project.client || project.duration) && (
            <div className="grid grid-cols-2 gap-4 mb-5">
              {project.client && (
                <div>
                  <div className="flex items-center space-x-2 text-text-muted mb-1">
                    <Icon name="Building" size={14} />
                    <span className="text-xs font-medium">Client</span>
                  </div>
                  <p className="text-text-primary text-sm font-semibold">{project.client}</p>
                </div>
              )}
              {project.duration && (
                <div>
                  <div className="flex items-center space-x-2 text-text-muted mb-1">
                    <Icon name="Calendar" size={14} />
                    <span className="text-xs font-medium">Timeline</span>
                  </div>
                  <p className="text-text-primary text-sm font-semibold">{project.duration}</p>
                </div>
              )}
            </div>
          )}

          {/* Key Results */}
          {project.results?.length > 0 && (
            <div className="bg-success/10 border border-success/20 rounded-xl p-4 mb-5">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="TrendingUp" size={16} className="text-success-light" />
                <h3 className="font-heading font-semibold text-sm text-success-light">Key Results</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {project.results.map((result, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={14} className="text-success-light flex-shrink-0" />
                    <span className="text-xs text-success-light/80">{result}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Services */}
          {project.services?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-xs text-text-primary mb-2 uppercase tracking-wider">Services Provided</h3>
              <div className="flex flex-wrap gap-1.5">
                {project.services.map((service, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 border border-accent/20 text-accent-light"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="flex flex-wrap gap-3">
            {project.liveUrl && (
              <Button
                variant="primary"
                size="md"
                iconName="ExternalLink"
                iconPosition="right"
                onClick={() => window.open(project.liveUrl, '_blank')}
                title={`Launch live demo for ${project.title}`}
                aria-label={`Open external window to view live deployed version of ${project.title}`}
              >
                View Live
              </Button>
            )}

            {project.codeUrl && (
              <Button
                variant="outline"
                size="md"
                iconName="Github"
                iconPosition="right"
                className="hover:bg-white/5 border-white/20"
                onClick={() => window.open(project.codeUrl, '_blank')}
                title={`Open Source Code repository for ${project.title}`}
                aria-label={`Open external window to view source code of ${project.title}`}
              >
                Source Code
              </Button>
            )}
            
            <Link 
              to={`/projects/${project.id}`} 
              className="w-auto"
              title={`Read detailed Case Study: ${project.title}`}
              aria-label={`Navigate to the comprehensive architectural case study for the ${project.title} project`}
            >
              <Button
                variant="outline"
                size="md"
                iconName="ArrowRight"
                iconPosition="right"
                className="w-full"
              >
                Case Study
              </Button>
            </Link>
      
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedProject;